// ---------------------------------------------------------------------------
// YouAreYou (uru) — useContactMatch hook
//
// Fetches device contacts once on mount (native only), builds a lookup map
// of E.164 phone → DeviceContact, and exposes:
//
//   isInAddressBook(phone)  → boolean
//   getContact(phone)       → DeviceContact | undefined
//   addToContacts(input)    → Promise<void>  (opens native contact editor)
//
// On web (isNative === false), all lookups return false/undefined.
// This hook is safe to call unconditionally — it degrades gracefully.
// ---------------------------------------------------------------------------

import { useState, useEffect, useCallback } from 'react'
import {
  getDeviceContacts,
  createDeviceContact,
  normalisePhone,
  type DeviceContact,
  type NewContactInput,
} from '@/lib/contacts'
import { isNative } from '@/lib/platform'

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UseContactMatchResult {
  /** True once the contact list has been fetched (or confirmed not available). */
  ready: boolean
  /** Look up whether a phone number is in the device address book. */
  isInAddressBook: (phone: string | null | undefined) => boolean
  /** Retrieve the matching DeviceContact for a phone number, if any. */
  getContact: (phone: string | null | undefined) => DeviceContact | undefined
  /** Open the native contact creation sheet pre-filled with the given data. */
  addToContacts: (input: NewContactInput) => Promise<void>
}

export function useContactMatch(): UseContactMatchResult {
  // Map of normalised E.164 phone → DeviceContact
  const [phoneMap, setPhoneMap] = useState<Map<string, DeviceContact>>(new Map())
  const [ready, setReady] = useState(!isNative) // On web, immediately ready (empty)

  useEffect(() => {
    if (!isNative) return

    let cancelled = false

    getDeviceContacts().then((contacts) => {
      if (cancelled) return
      const map = new Map<string, DeviceContact>()
      for (const contact of contacts) {
        for (const phone of contact.phones) {
          map.set(phone, contact)
        }
      }
      setPhoneMap(map)
      setReady(true)
    })

    return () => {
      cancelled = true
    }
  }, [])

  const isInAddressBook = useCallback(
    (phone: string | null | undefined): boolean => {
      const normalised = normalisePhone(phone)
      if (!normalised) return false
      return phoneMap.has(normalised)
    },
    [phoneMap],
  )

  const getContact = useCallback(
    (phone: string | null | undefined): DeviceContact | undefined => {
      const normalised = normalisePhone(phone)
      if (!normalised) return undefined
      return phoneMap.get(normalised)
    },
    [phoneMap],
  )

  const addToContacts = useCallback(async (input: NewContactInput): Promise<void> => {
    await createDeviceContact(input)
  }, [])

  return { ready, isInAddressBook, getContact, addToContacts }
}
